// Entity-State-Store. V1-Pendant: useState-Block in useEntityState.js.
// Hält EVE's Bewusstseins-Metriken. Kein persist — startet bei jedem Mount fresh
// mit den Default-Werten (V1-Verhalten).

import { create } from 'zustand';
import {
  DEFAULT_AUTONOMY,
  TOTAL_NODES,
  applyMarketEvent,
  calculateConfidence,
  calculateHealth,
  canTransition,
  clamp01,
  emotionalForAutonomy,
  emotionalForState,
} from '@/lib/entity-state';
import type {
  EmotionalContext,
  EntityState,
  EntityStatusSummary,
  MarketEvent,
} from '@/types/eve-state';

export interface EntityStateData {
  entityState: EntityState;
  emotionalContext: EmotionalContext;
  autonomyLevel: number;
  decisionConfidence: number;
  activeNodes: number;
  totalNodes: number;
  cognitiveLoad: number;
  stressLevel: number;
  focusLevel: number;
}

export interface EntityStateActions {
  /** Versucht State-Übergang. Gibt true zurück wenn erlaubt (V1 silent-skip). */
  transitionState: (next: EntityState) => boolean;
  /** Setzt Autonomy + ggf. Emotional-Context-Override (>80 → CONFIDENT, <30 → CAUTIOUS). */
  adjustAutonomy: (level: number) => void;
  /** Wendet einen MarketEvent an — delegiert an applyMarketEvent + State-Updates. */
  handleMarketEvent: (event: MarketEvent) => void;
  /** Manuelle Setter — nützlich für Tests + externe Trigger. */
  setEmotionalContext: (ctx: EmotionalContext) => void;
  setCognitiveLoad: (load: number) => void;
  setStressLevel: (level: number) => void;
  setFocusLevel: (level: number) => void;
  /** Tick-Reducer (V1: alle 5s natürliche Reduktion). */
  applyCognitiveTick: () => void;
  /** Node-Event-Tick (V1: alle 10s 5% chance auf node-event). */
  applyNodeTick: () => void;
  /** Liefert die volle Status-Summary für UI / Prompt. */
  getStatusSummary: () => EntityStatusSummary;
  /** Reset auf Default-Werte. */
  reset: () => void;
}

export type EntityStore = EntityStateData & EntityStateActions;

const initialState: EntityStateData = {
  entityState: 'MONITORING',
  emotionalContext: 'NEUTRAL',
  autonomyLevel: DEFAULT_AUTONOMY,
  decisionConfidence: 87,
  activeNodes: TOTAL_NODES,
  totalNodes: TOTAL_NODES,
  cognitiveLoad: 45,
  stressLevel: 20,
  focusLevel: 75,
};

/**
 * Recompute decisionConfidence whenever inputs change. V1 hatte das in einem
 * useEffect — wir machen es hier inline in jeder relevanten Action.
 */
function recomputeConfidence(state: EntityStateData): number {
  return calculateConfidence({
    autonomyLevel: state.autonomyLevel,
    stressLevel: state.stressLevel,
    focusLevel: state.focusLevel,
    cognitiveLoad: state.cognitiveLoad,
  });
}

export const useEntityStateStore = create<EntityStore>()((set, get) => ({
  ...initialState,

  transitionState: (next) => {
    const current = get().entityState;
    if (!canTransition(current, next)) return false;
    const emotional = emotionalForState(next);
    set((state) => {
      const partial: Partial<EntityStateData> = { entityState: next };
      if (emotional) partial.emotionalContext = emotional;
      // V1-Side-effects pro Ziel-State
      if (next === 'STRESSED') {
        partial.stressLevel = clamp01(state.stressLevel + 20);
      } else if (next === 'EXECUTING') {
        partial.focusLevel = clamp01(state.focusLevel + 10);
      } else if (next === 'ANALYZING') {
        partial.cognitiveLoad = clamp01(state.cognitiveLoad + 15);
      }
      const merged = { ...state, ...partial };
      return { ...partial, decisionConfidence: recomputeConfidence(merged) };
    });
    return true;
  },

  adjustAutonomy: (level) => {
    const clamped = clamp01(level);
    const emo = emotionalForAutonomy(clamped);
    set((state) => {
      const merged = { ...state, autonomyLevel: clamped };
      return {
        autonomyLevel: clamped,
        emotionalContext: emo ?? state.emotionalContext,
        decisionConfidence: recomputeConfidence(merged),
      };
    });
  },

  handleMarketEvent: (event) => {
    const effect = applyMarketEvent(event);
    if (effect.targetState === null) return;
    // V1-Verhalten: Delta-Werte (stress/cognitive/nodes) gelten unbedingt,
    // die State-Transition selbst scheitert silent wenn nicht erlaubt.
    set((state) => {
      const transitionAllowed = canTransition(state.entityState, effect.targetState!);
      const nextState = transitionAllowed ? effect.targetState! : state.entityState;
      const newStress = clamp01(state.stressLevel + effect.stressDelta);
      const newCognitive = clamp01(state.cognitiveLoad + effect.cognitiveDelta);
      const newActiveNodes = effect.resetActiveNodes
        ? state.totalNodes
        : Math.max(15, Math.min(state.totalNodes, state.activeNodes + effect.nodesDelta));
      const transitionEmotional = transitionAllowed
        ? (emotionalForState(effect.targetState!) ?? state.emotionalContext)
        : state.emotionalContext;
      const targetEmotional = effect.emotionalOverride ?? transitionEmotional;
      const merged: EntityStateData = {
        ...state,
        entityState: nextState,
        emotionalContext: targetEmotional,
        stressLevel: newStress,
        cognitiveLoad: newCognitive,
        activeNodes: newActiveNodes,
      };
      return {
        entityState: nextState,
        emotionalContext: targetEmotional,
        stressLevel: newStress,
        cognitiveLoad: newCognitive,
        activeNodes: newActiveNodes,
        decisionConfidence: recomputeConfidence(merged),
      };
    });
  },

  setEmotionalContext: (ctx) => set({ emotionalContext: ctx }),

  setCognitiveLoad: (load) => {
    set((state) => {
      const merged = { ...state, cognitiveLoad: clamp01(load) };
      return { cognitiveLoad: clamp01(load), decisionConfidence: recomputeConfidence(merged) };
    });
  },

  setStressLevel: (level) => {
    set((state) => {
      const merged = { ...state, stressLevel: clamp01(level) };
      return { stressLevel: clamp01(level), decisionConfidence: recomputeConfidence(merged) };
    });
  },

  setFocusLevel: (level) => {
    set((state) => {
      const merged = { ...state, focusLevel: clamp01(level) };
      return { focusLevel: clamp01(level), decisionConfidence: recomputeConfidence(merged) };
    });
  },

  applyCognitiveTick: () => {
    // V1 5s-Tick: cognitive -2, stress -1, focus ± random(-5..+5) clampt
    set((state) => {
      const newCognitive = Math.max(20, state.cognitiveLoad - 2);
      const newStress = Math.max(10, state.stressLevel - 1);
      const change = (Math.random() - 0.5) * 10;
      const newFocus = Math.max(30, Math.min(100, state.focusLevel + change));
      const merged: EntityStateData = {
        ...state,
        cognitiveLoad: newCognitive,
        stressLevel: newStress,
        focusLevel: newFocus,
      };
      return {
        cognitiveLoad: newCognitive,
        stressLevel: newStress,
        focusLevel: newFocus,
        decisionConfidence: recomputeConfidence(merged),
      };
    });
  },

  applyNodeTick: () => {
    // V1 10s-Tick: 5% chance node-event
    if (Math.random() >= 0.05) return;
    set((state) => {
      if (state.activeNodes < state.totalNodes && Math.random() < 0.7) {
        return { activeNodes: Math.min(state.totalNodes, state.activeNodes + 1) };
      }
      if (state.activeNodes > 15 && Math.random() < 0.3) {
        return { activeNodes: Math.max(15, state.activeNodes - 1) };
      }
      return {};
    });
  },

  getStatusSummary: () => {
    const s = get();
    return {
      state: s.entityState,
      emotional: s.emotionalContext,
      autonomy: s.autonomyLevel,
      confidence: s.decisionConfidence,
      nodes: { active: s.activeNodes, total: s.totalNodes },
      cognitive: { load: s.cognitiveLoad, stress: s.stressLevel, focus: s.focusLevel },
      health: calculateHealth({
        activeNodes: s.activeNodes,
        totalNodes: s.totalNodes,
        stressLevel: s.stressLevel,
        focusLevel: s.focusLevel,
        cognitiveLoad: s.cognitiveLoad,
        decisionConfidence: s.decisionConfidence,
      }),
    };
  },

  reset: () => set({ ...initialState }),
}));
